import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

const stories = [
  {
    descriptor: 'Global Retail Technology MNC',
    hq: 'Australia-headquartered',
    outcome: 'Built the technology leadership team and core capability functions for their GCC, from founding hire through to operational scale.',
    roles: ['VP Engineering', 'Head of Data & Analytics', 'Director of Product', 'Engineering Managers'],
    tag: 'GCC Setup & Scaling',
    note: 'Client identity withheld pending approval',
  },
  {
    descriptor: 'Series B Enterprise SaaS',
    hq: 'US-headquartered',
    outcome: 'Identified and placed the founding CTO and multiple engineering directors in a focused, sequenced search engagement.',
    roles: ['Country CTO', 'Director of Backend Engineering', 'Director of Infrastructure', 'Head of QA'],
    tag: 'Tech Leadership Search',
    note: 'Client identity withheld pending approval',
  },
  {
    descriptor: 'European Technology Conglomerate GCC',
    hq: 'Europe-headquartered',
    outcome: 'Scaled a Data & AI function across multiple specialisations, from a founding team to a fully operational centre of excellence.',
    roles: ['Head of Data Science', 'Principal ML Engineer', 'Data Engineering Lead', 'Analytics Director'],
    tag: 'GCC Scaling',
    note: 'Client identity withheld pending approval',
  },
]

export default function SuccessStoriesSection() {
  return (
    <section
      data-testid="success-stories-section"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#F6F7F9]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20"
        >
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
              Work
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
              Selected<br />
              <span className="font-light text-[#16365F]">outcomes</span>
            </h2>
          </div>
          <p className="text-xs font-body text-[#0A0A0A]/40 max-w-xs leading-relaxed">
            All engagements anonymised pending client confirmation. Outcomes reflect mandate scope, not independently verified figures.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-[#E5E7EB]">
          {stories.map((story, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
              className="py-10 px-0 md:px-6 border-b lg:border-b-0 lg:border-r border-[#E5E7EB] last:border-r-0 first:pl-0"
              data-testid={`success-story-${i + 1}`}
            >
              <p className="text-[9px] tracking-[0.3em] uppercase text-[#16365F] font-body font-semibold mb-5">
                {story.tag}
              </p>
              <h3 className="text-base font-heading font-black text-[#0A0A0A] mb-1 tracking-tight">
                {story.descriptor}
              </h3>
              <p className="text-xs font-body text-[#0A0A0A]/40 mb-6">{story.hq}</p>
              <p className="text-sm font-body text-[#0A0A0A]/70 leading-relaxed mb-6 font-medium">
                {story.outcome}
              </p>
              <ul className="space-y-1.5 mb-8">
                {story.roles.map(role => (
                  <li key={role} className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-[#16365F]" />
                    <span className="text-xs font-body text-[#0A0A0A]/45">{role}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[9px] tracking-wider uppercase text-[#0A0A0A]/20 font-body italic">
                {story.note}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16"
        >
          <Link
            to="/contact"
            data-testid="stories-cta"
            className="inline-flex items-center gap-3 text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 hover:text-[#0B1F3A] hover:border-[#0B1F3A] transition-colors"
          >
            Discuss your mandate <ArrowRight size={11} />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
